import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ParentMessageType,
  IframeToParentMessage,
  IframeMessageType,
  SectionBoundsResponsePayload,
  SectionClickedPayload,
  SectionHoveredPayload,
  FieldClickedPayload,
  TextEditedPayload,
  TextEditCompletePayload,
  ScrollPositionPayload,
  IframeResizedPayload,
  EditorReadyPayload,
  SectionUnhoveredPayload,
} from '../types/visualEditor';

export interface UseIframePostMessageOptions {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onEditorReady?: (payload: EditorReadyPayload) => void;
  onSectionClicked?: (payload: SectionClickedPayload) => void;
  onSectionHovered?: (payload: SectionHoveredPayload) => void;
  onSectionUnhovered?: (payload: SectionUnhoveredPayload) => void;
  onFieldClicked?: (payload: FieldClickedPayload) => void;
  onTextEdited?: (payload: TextEditedPayload) => void;
  onTextEditComplete?: (payload: TextEditCompletePayload) => void;
  onSectionBounds?: (payload: SectionBoundsResponsePayload) => void;
  onScrollPosition?: (payload: ScrollPositionPayload) => void;
  onIframeResized?: (payload: IframeResizedPayload) => void;
}

export interface UseIframePostMessageReturn {
  sendMessage: <T>(type: ParentMessageType, payload: T) => void;
  isReady: boolean;
  lastMessage: IframeToParentMessage | null;
  // Convenience methods
  selectSection: (sectionIndex: number) => void;
  deselectAll: () => void;
  highlightSection: (sectionIndex: number) => void;
  unhighlightSection: (sectionIndex: number) => void;
  enableInlineEdit: (sectionIndex: number, fieldName: string) => void;
  disableInlineEdit: () => void;
  updateFieldValue: (sectionIndex: number, fieldName: string, value: string) => void;
  scrollToSection: (sectionIndex: number) => void;
  requestSectionBounds: () => Promise<SectionBoundsResponsePayload>;
  initEditor: () => void;
  refreshPreview: () => void;
}

export function useIframePostMessage(
  options: UseIframePostMessageOptions
): UseIframePostMessageReturn {
  const {
    iframeRef,
    onEditorReady,
    onSectionClicked,
    onSectionHovered,
    onSectionUnhovered,
    onFieldClicked,
    onTextEdited,
    onTextEditComplete,
    onSectionBounds,
    onScrollPosition,
    onIframeResized,
  } = options;

  const [isReady, setIsReady] = useState(false);
  const [lastMessage, setLastMessage] = useState<IframeToParentMessage | null>(null);

  // Store pending bounds requests for promise resolution
  const pendingBoundsRequests = useRef<Map<string, {
    resolve: (value: SectionBoundsResponsePayload) => void;
    reject: (reason: Error) => void;
  }>>(new Map());

  // Send message to iframe
  const sendMessage = useCallback(<T>(type: ParentMessageType, payload: T) => {
    if (!iframeRef.current?.contentWindow) {
      console.warn('[useIframePostMessage] Iframe not ready, cannot send message:', type);
      return;
    }

    const message = {
      type,
      payload,
      timestamp: Date.now(),
    };

    iframeRef.current.contentWindow.postMessage(message, '*');
  }, [iframeRef]);

  // Handle incoming messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Basic validation - ensure message has expected structure
      if (!event.data || typeof event.data.type !== 'string') {
        return;
      }

      const message = event.data as IframeToParentMessage;

      // Debug logging - remove after fixing
      console.log('[PostMessage] Received:', message.type, message.payload);
      setLastMessage(message);

      const type = message.type as IframeMessageType;
      const payload = message.payload;

      switch (type) {
        case 'EDITOR_READY':
          setIsReady(true);
          onEditorReady?.(payload as EditorReadyPayload);
          break;

        case 'SECTION_CLICKED':
          onSectionClicked?.(payload as SectionClickedPayload);
          break;

        case 'SECTION_HOVERED':
          onSectionHovered?.(payload as SectionHoveredPayload);
          break;

        case 'SECTION_UNHOVERED':
          onSectionUnhovered?.(payload as SectionUnhoveredPayload);
          break;

        case 'FIELD_CLICKED':
          onFieldClicked?.(payload as FieldClickedPayload);
          break;

        case 'TEXT_EDITED':
          onTextEdited?.(payload as TextEditedPayload);
          break;

        case 'TEXT_EDIT_COMPLETE':
          onTextEditComplete?.(payload as TextEditCompletePayload);
          break;

        case 'SECTION_BOUNDS': {
          const boundsPayload = payload as SectionBoundsResponsePayload;
          onSectionBounds?.(boundsPayload);

          // Resolve pending promise if any
          const pending = pendingBoundsRequests.current.get(boundsPayload.requestId);
          if (pending) {
            pending.resolve(boundsPayload);
            pendingBoundsRequests.current.delete(boundsPayload.requestId);
          }
          break;
        }

        case 'SCROLL_POSITION':
          onScrollPosition?.(payload as ScrollPositionPayload);
          break;

        case 'IFRAME_RESIZED':
          onIframeResized?.(payload as IframeResizedPayload);
          break;

        default:
          // Unknown message type - ignore
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    onEditorReady,
    onSectionClicked,
    onSectionHovered,
    onSectionUnhovered,
    onFieldClicked,
    onTextEdited,
    onTextEditComplete,
    onSectionBounds,
    onScrollPosition,
    onIframeResized,
  ]);

  // Convenience methods
  const selectSection = useCallback((sectionIndex: number) => {
    sendMessage('SELECT_SECTION', { sectionIndex });
  }, [sendMessage]);

  const deselectAll = useCallback(() => {
    sendMessage('DESELECT_ALL', {});
  }, [sendMessage]);

  const highlightSection = useCallback((sectionIndex: number) => {
    sendMessage('HIGHLIGHT_SECTION', { sectionIndex });
  }, [sendMessage]);

  const unhighlightSection = useCallback((sectionIndex: number) => {
    sendMessage('UNHIGHLIGHT_SECTION', { sectionIndex });
  }, [sendMessage]);

  const enableInlineEdit = useCallback((sectionIndex: number, fieldName: string) => {
    sendMessage('ENABLE_INLINE_EDIT', { sectionIndex, fieldName });
  }, [sendMessage]);

  const disableInlineEdit = useCallback(() => {
    sendMessage('DISABLE_INLINE_EDIT', {});
  }, [sendMessage]);

  const updateFieldValue = useCallback((sectionIndex: number, fieldName: string, value: string) => {
    sendMessage('UPDATE_FIELD_VALUE', { sectionIndex, fieldName, value });
  }, [sendMessage]);

  const scrollToSection = useCallback((sectionIndex: number) => {
    sendMessage('SCROLL_TO_SECTION', { sectionIndex });
  }, [sendMessage]);

  const requestSectionBounds = useCallback((): Promise<SectionBoundsResponsePayload> => {
    return new Promise((resolve, reject) => {
      const requestId = `bounds-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      // Set up timeout for the request
      const timeout = setTimeout(() => {
        pendingBoundsRequests.current.delete(requestId);
        reject(new Error('Section bounds request timed out'));
      }, 5000);

      // Store the promise handlers
      pendingBoundsRequests.current.set(requestId, {
        resolve: (value) => {
          clearTimeout(timeout);
          resolve(value);
        },
        reject: (reason) => {
          clearTimeout(timeout);
          reject(reason);
        },
      });

      sendMessage('GET_SECTION_BOUNDS', { requestId });
    });
  }, [sendMessage]);

  const initEditor = useCallback(() => {
    sendMessage('EDITOR_INIT', {});
  }, [sendMessage]);

  const refreshPreview = useCallback(() => {
    sendMessage('REFRESH_PREVIEW', {});
  }, [sendMessage]);

  return {
    sendMessage,
    isReady,
    lastMessage,
    selectSection,
    deselectAll,
    highlightSection,
    unhighlightSection,
    enableInlineEdit,
    disableInlineEdit,
    updateFieldValue,
    scrollToSection,
    requestSectionBounds,
    initEditor,
    refreshPreview,
  };
}
